package nl.astraeus.wiki.service

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLLinkElement
import org.w3c.dom.HTMLScriptElement

enum class ResourceType {
  JS,
  CSS,
}

enum class ResourceStatus {
  LOADING,
  LOADED,
  FAILED,
}

data class Resource(
  var status: ResourceStatus,
  val type: ResourceType,
  val name: String,
  val loadedCallbacks: MutableList<() -> Unit> = mutableListOf()
) {
  fun load() {
    when (type) {
      ResourceType.JS -> {
        val script = document.createElement("script") as HTMLScriptElement
        script.setAttribute("src", name)
        document.body?.appendChild(script)
        script.onload = {
          executeCallbacks()
        }
        script.onerror = { _, _, _, _, _ ->
          console.error("Error loading script: $name")
          status = ResourceStatus.FAILED
        }
      }

      ResourceType.CSS -> {
        val style = document.createElement("link") as HTMLLinkElement
        style.setAttribute("rel", "stylesheet")
        style.setAttribute("href", name)
        document.head?.appendChild(style)
        style.onload = {
          executeCallbacks()
        }
        style.onerror = { _, _, _, _, _ ->
          console.error("Error loading style: $name")
          status = ResourceStatus.FAILED
        }
      }
    }
  }

  private fun executeCallbacks() {
    window.setTimeout({
      status = ResourceStatus.LOADED
      loadedCallbacks.forEach {
        it()
      }
      loadedCallbacks.clear()
    }, 0)
  }

}

object ResourceService {
  val loadedResources = mutableMapOf<String, Resource>()

  fun isLoading(): Boolean {
    return loadedResources.any { it.value.status == ResourceStatus.LOADING }
  }

  fun loadResource(
    type: ResourceType,
    name: String,
    loadedCallback: () -> Unit = {}
  ) {
    when (loadedResources[name]?.status) {
      ResourceStatus.LOADED -> {
        window.setTimeout({
          loadedCallback()
        })
      }

      ResourceStatus.LOADING -> {
        loadedResources[name]?.loadedCallbacks?.add(loadedCallback)
      }

      else -> {
        val resource = Resource(
          ResourceStatus.LOADING,
          type,
          name
        )
        loadedResources[name] = resource

        resource.loadedCallbacks.add(loadedCallback)
        resource.load()
      }
    }
  }

}
